import React, { useEffect, useState } from 'react';
import { Camera, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { fmtDateTime, portalApi, portalFileUrl } from './portalApi';
import { Card, Empty, PageHeader, Spinner, WoChip } from './portalUi';

const Lightbox = ({ photos, idx, onClose, onMove }) => {
  const p = photos[idx];
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); if (e.key === 'ArrowRight') onMove(1); if (e.key === 'ArrowLeft') onMove(-1); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onMove]);
  return (
    <div className="fixed inset-0 z-[90] bg-[#0B192C]/95 flex flex-col" data-testid="photo-lightbox">
      <div className="flex items-center justify-between px-4 h-14 text-white shrink-0">
        <div className="min-w-0"><span className="p-mono font-bold text-blue-200">WO {p.work_order_number}</span>{p.title && <span className="text-sm text-blue-100/80 ml-2 truncate">{p.title}</span>}</div>
        <button type="button" onClick={onClose} className="h-10 w-10 inline-flex items-center justify-center rounded-lg hover:bg-white/10" aria-label="Close" data-testid="lightbox-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 relative flex items-center justify-center px-12 min-h-0">
        <button type="button" onClick={() => onMove(-1)} className="absolute left-2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center" aria-label="Previous" data-testid="lightbox-prev"><ChevronLeft className="w-6 h-6" /></button>
        <img src={portalFileUrl(`/photos/${p.id}/image`)} alt={p.caption || p.filename} className="max-h-full max-w-full object-contain rounded-lg shadow-2xl" />
        <button type="button" onClick={() => onMove(1)} className="absolute right-2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center" aria-label="Next" data-testid="lightbox-next"><ChevronRight className="w-6 h-6" /></button>
      </div>
      <div className="px-4 py-3 text-white text-sm shrink-0 flex flex-wrap items-center justify-between gap-2">
        <span>{p.caption || <span className="text-blue-200/70">No caption</span>}</span>
        <span className="text-xs text-blue-200/80 p-mono">{p.uploaded_by_name ? `${p.uploaded_by_name} · ` : ''}{fmtDateTime(p.created_at)} · {idx + 1}/{photos.length}</span>
      </div>
    </div>
  );
};

export default function PortalPhotos() {
  const [photos, setPhotos] = useState(null);
  const [idx, setIdx] = useState(-1);
  useEffect(() => { portalApi.get('/photos').then((r) => setPhotos(r.data)); }, []);
  if (!photos) return <Spinner />;
  const groups = photos.reduce((acc, p) => { const k = p.work_order_number; (acc[k] = acc[k] || { title: p.title, list: [] }).list.push(p); return acc; }, {});
  const move = (d) => setIdx((i) => (i + d + photos.length) % photos.length);

  return (
    <div data-testid="portal-photos">
      <PageHeader overline="Job photos" title="Progress from the floor" subtitle="Photos our crew takes on your work orders, released to you after a project manager reviews them." />
      {photos.length === 0 ? <Card><Empty icon={Camera} title="No photos released yet" text="As soon as our PM approves site photos for your work orders they show up here." /></Card>
        : Object.entries(groups).map(([wo, g]) => (
          <div key={wo} className="mb-7 p-rise">
            <div className="flex items-center gap-2 mb-3"><WoChip wo={wo} title={g.title} /><span className="text-xs text-slate-400">{g.list.length} photo{g.list.length === 1 ? '' : 's'}</span></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid={`photo-group-${wo}`}>
              {g.list.map((p) => (
                <button type="button" key={p.id} onClick={() => setIdx(photos.indexOf(p))} className="group relative aspect-[4/3] rounded-xl overflow-hidden border border-slate-200 bg-slate-100 text-left" data-testid={`photo-${p.id}`}>
                  <img src={portalFileUrl(`/photos/${p.id}/image`)} alt={p.caption || p.filename} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0B192C]/85 to-transparent p-2.5 pt-8 text-white">
                    <div className="text-xs font-semibold truncate">{p.caption || p.filename}</div>
                    <div className="text-[10px] text-blue-100/80 p-mono">{fmtDateTime(p.created_at)}</div>
                  </div>
                </button>))}
            </div>
          </div>))}
      {idx >= 0 && <Lightbox photos={photos} idx={idx} onClose={() => setIdx(-1)} onMove={move} />}
    </div>
  );
}
